import { EXTERNAL_EVENTS } from '@repo/common';
import { externalCommunication, externalState } from './ExternalCommunication';
import { useAppStore, deriveCampaignViewState } from '../../../store/app/appStore';
import { useGameStartStore } from 'features/games';
import { useCampaignsStore } from 'features/campaigns';
import { sectionNavigationTracker } from '../SectionNavigationTracker';
import { trackerManager } from '../managers/TrackerManager';
import type { IExternalBalance } from '../../../types/external/IExternalBalance';
import type { ISettings } from '../../../types/models/ISettings';

export function wireExternalMessages() {
    trackerManager.startStep('Wiring external message handlers');
    externalCommunication.onMessage(EXTERNAL_EVENTS.BALANCE, (value: IExternalBalance) => {
        useAppStore.getState().updateBalance(value);
    });

    // Settings updates
    externalCommunication.onMessage(EXTERNAL_EVENTS.SETTINGS, (value: ISettings) => {
        const source = value.openType === 'ingame' ? 'widget' : 'banner';

        if (value.campaignOnly?.type) sectionNavigationTracker.trackSectionNavigation(value.campaignOnly?.type, source);

        const prevSettings = useAppStore.getState().settings;
        const prevCampaignMode = prevSettings.campaignOnly?.type;

        // Always set campaignOnly explicitly (defaulting to undefined) so a SETTINGS
        // message that omits it clears any stale campaign-only mode left over from
        // a previous open, instead of the shallow merge in updateSettings
        const normalizedSettings: ISettings = {
            ...value,
            campaignOnly: value.campaignOnly,
        };

        useAppStore.getState().updateSettings(normalizedSettings);

        const nextCampaignMode = normalizedSettings.campaignOnly?.type;
        // Entering, exiting, or switching campaign-only mode: derive the matching
        // selectedSection/viewMode synchronously, so the next render shows the
        // correct view immediately instead of flashing the previous one.
        if (prevCampaignMode !== nextCampaignMode) {
            useAppStore.setState(deriveCampaignViewState(normalizedSettings));
        }


        const campaignOnly = normalizedSettings.campaignOnly;
        if (campaignOnly?.campaignID) {
            useCampaignsStore.getState().setSelectedCampaignId(campaignOnly.campaignID, campaignOnly.type);
        }

        // Settings have landed — safe to render again if we were holding off after a hide.
        if (useAppStore.getState().awaitingSettingsAfterHide) {
            useAppStore.getState().setAwaitingSettingsAfterHide(false);
        }
        // Note: User data (playerId, sessionKey) and user settings (sound, language)
        // are tracked in appStore.updateSettings via userDataTracker
    });

    // Display events
    externalCommunication.onMessage(EXTERNAL_EVENTS.DISPLAY, (value: boolean) => {
        externalState.isVisible = value;
        useAppStore.getState().setVisible(value);

        if (value) {
            const { settingsRequestDone, setIdle, triggerInitialization } = useAppStore.getState();
            setIdle(false);

            // Trigger initialization only if settings not ready yet
            if (!settingsRequestDone) {
                triggerInitialization();
            }
        }
    });

    // Mute events
    externalCommunication.onMessage(EXTERNAL_EVENTS.MUTE, (value: boolean) => {
        externalState.soundMuted = value;
        useAppStore.getState().updateSettings({ mute: value });
        // Note: Sound setting is tracked in appStore.updateSettings
    });

    // Background music mute events
    externalCommunication.onMessage(EXTERNAL_EVENTS.BG_MUSIC_MUTE, (value: boolean) => {
        externalState.bgMusicMuted = value;
        useAppStore.getState().updateSettings({ bgMusicMute: value });
    });

    // Defense code events
    externalCommunication.onMessage(EXTERNAL_EVENTS.DEFENSE_CODE, (value: string) => {
        externalState.defenseCode = value;
        // Update game start store with defense code - use direct import
        setDefenseCodeInStore(value);
    });

    // Last played games events
    externalCommunication.onMessage('lastPlayedGames', (value: string[]) => {
        externalState.lastPlayedGames = Array.isArray(value) ? value : [];
    });

    // Home click events - trigger exit like old lobby
    externalCommunication.onMessage('homeClick', (_value: boolean) => {
        externalCommunication.sendExit();
    });
    trackerManager.completeStep('Wiring external message handlers');
    trackerManager.completeStep('ExternalCommunicationService initialization');
}

function setDefenseCodeInStore(value: string) {
    try {
        useGameStartStore.getState().setDefenseCode(value);
    } catch (error) {
        console.error('[ExternalCommunication] Failed to set defense code:', error);
    }
}
